/* RESPONSIBLE TEAM: team-reporting */

import {
  ChartSchema,
  ChartDefinition,
  Timezone,
  RowType,
} from '@/features/report-snapshots/api/schema';
import type { ChartType, HighchartsSeriesType } from '@/features/report-snapshots/types';
import { formatDateLabel, formatTimestamp } from '@/features/report-snapshots/formatters/timestamp';
import { I18n } from '@lingui/core';
import { getStackedNonSegmentSeries } from '@/features/report-snapshots/components/charts/utils/chart-stacked-non-segment-data';
import {
  assignColorToSeriesItem,
  assignColorToSeries,
} from '@/features/report-snapshots/components/charts/utils/chart-series-colors';
import {
  PieChartSeriesData,
  getPieSeries,
} from '@/features/report-snapshots/components/charts/utils/chart-pie-data';
import {
  extractNumericValue,
  translateCellName,
} from '@/features/report-snapshots/utils/chart-data-helpers';
import { normalizeCellValue } from '@/features/report-snapshots/utils/common';

interface HeaderDefinition {
  value: string | number;
  unit: string;
}

export interface ChartSeriesDataPoint {
  y: number | null;
  name: string;
  color?: string;
  formattedValue?: string;
}

export interface ChartSeries {
  name: string;
  type: HighchartsSeriesType;
  data: ChartSeriesDataPoint[];
  color?: string;
  yAxis?: number;
}

export function getSeries(
  schema: ChartSchema,
  chartType: ChartType,
  chartDefinition: ChartDefinition,
  timezone: Timezone,
  i18n: I18n,
): ChartSeries[] | PieChartSeriesData[] {
  switch (chartType) {
    case 'pie': {
      return getPieSeries(schema, chartDefinition, timezone, i18n);
    }
    default:
      return getBaseSeries(schema, chartType, chartDefinition, timezone, i18n);
  }
}

function getRowName(row: RowType): string {
  const idCell = row['0'];
  if (typeof idCell === 'string' || typeof idCell === 'number') {
    return String(idCell);
  }
  return '';
}

function getRowTotal(row: RowType): number {
  return Object.entries(row)
    .filter(([key]) => key !== '0') // skip the first cell
    .reduce((sum, [, value]) => sum + (extractNumericValue(value) ?? 0), 0);
}

export function orderCategoriesBySum(rawData: RowType[]): string[] {
  return rawData
    .map((row) => ({ name: getRowName(row), total: getRowTotal(row) }))
    .sort((a, b) => b.total - a.total)
    .map(({ name }) => name);
}

export function orderSeriesByMaxYValues(series: ChartSeries[], chartType: ChartType) {
  if (chartType === 'line' || chartType === 'area') {
    series.sort((a, b) => {
      const maxA = Math.max(...a.data.map((p) => p.y ?? 0));
      const maxB = Math.max(...b.data.map((p) => p.y ?? 0));
      return maxA - maxB;
    });
  }
}

function getBaseSeries(
  schema: ChartSchema,
  chartType: ChartType,
  chartDefinition: ChartDefinition,
  timezone: Timezone,
  i18n: I18n,
): ChartSeries[] {
  // Special handling for stacked non-segmented series visualization
  if (chartDefinition.stacked && !chartDefinition.segmentBy) {
    return getStackedNonSegmentSeries(schema, chartType, chartDefinition, timezone, i18n);
  }

  const headers = extractHeaders(schema, chartDefinition, timezone, i18n);
  const rawData = schema.values;
  const columnDefinitions = schema.columnDefinitions;

  // only reorder if viewBy is NOT time
  const dataOrderedBySum =
    chartDefinition.viewBy === `time`
      ? rawData.map((r) => r[0]) // keep original channel order
      : orderCategoriesBySum(rawData);

  const isCombo = chartType === 'combo';

  const series = headers.map((header, index) => {
    const formattedDataPoint = dataOrderedBySum.map((cellName) => {
      const row = rawData.find((r) => r['0'] === cellName);
      const rowIndex = index + 1;
      let cellValue = row?.[String(rowIndex)];

      cellValue = normalizeCellValue(cellValue, columnDefinitions[rowIndex]);

      return {
        y: cellValue,
        name: cellName,
      };
    });

    const seriesItem: ChartSeries = {
      name: String(header.value),
      type: (isCombo ? (index === 0 ? 'column' : 'line') : chartType) as HighchartsSeriesType,
      data: formattedDataPoint as ChartSeriesDataPoint[],
      ...(isCombo ? { yAxis: index === 0 ? 0 : 1 } : {}),
    };

    assignColorToSeriesItem(seriesItem, chartDefinition);
    return seriesItem;
  });

  // Special ordering for line and area charts: order series by their max y values
  orderSeriesByMaxYValues(series, chartType);

  assignColorToSeries(series, chartDefinition);
  return series;
}

function extractHeaders(
  schema: ChartSchema,
  chartDefinition: ChartDefinition,
  timezone: Timezone,
  i18n: I18n,
): HeaderDefinition[] {
  const headers = schema.headers?.slice(1) as HeaderDefinition[];

  if (chartDefinition.segmentBy && chartDefinition.segmentByTimeInterval) {
    return headers.map((header) => {
      header.value = chartDefinition.segmentByTimeInterval
        ? formatTimestamp(
            chartDefinition.segmentByTimeInterval,
            header.value as number,
            timezone,
            i18n,
          )
        : formatDateLabel(header.value as number);

      return header;
    });
  }

  return headers.map((header, index) => {
    if (header.unit === 'timestamp' && typeof header.value === 'number') {
      header.value = chartDefinition.segmentByTimeInterval
        ? formatTimestamp(chartDefinition.segmentByTimeInterval, header.value, timezone, i18n)
        : formatDateLabel(header.value);
    } else if (!chartDefinition.segmentBy) {
      // For non-segmented charts, prefer the chartSeries label or metricDisplayName
      // over the raw header value. This correctly disambiguates series that share the
      // same metric ID but have distinct display names (e.g. CX Score breakdown).
      const seriesEntry = chartDefinition.chartSeries[index];
      const seriesLabel = seriesEntry.label ?? seriesEntry.metricDisplayName;
      header.value = seriesLabel ?? translateCellName(header.value as string, i18n);
    } else {
      header.value = translateCellName(header.value as string, i18n);
    }
    return header;
  });
}
